import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services';

const emptyChain = { list: null, next: null, loading: false, error: false };

export default function useReactionChain(reaction, enabled) {
  const available = Boolean(reaction.quote && !reaction.quote.unavailable && reaction.quote.user);
  const key = `${reaction.id}:${reaction.quote?.id || ''}:${available}`;
  const [state, setState] = useState({ ...emptyChain, key });
  // A fresh feed DTO can mark a source unavailable while this reader still
  // holds its old chain. Never render that cached author, even for one frame.
  const current = state.key === key ? state : emptyChain;
  const version = useRef(0);
  const pending = useRef(false);
  const controller = useRef(null);
  const requestedOffset = useRef(0);
  const load = useCallback(async (offset = 0) => {
    if (!available || pending.current) return;
    const generation = version.current;
    pending.current = true;
    const request = new AbortController();
    controller.current = request;
    requestedOffset.current = offset;
    setState(previous => ({ ...(previous.key === key ? previous : emptyChain), key, loading: true, error: false }));
    try {
      const response = await api.reactionQuotes({ id: reaction.id, offset, limit: 20 }, { signal: request.signal });
      if (generation !== version.current) return;
      if (!response.success || !Array.isArray(response.list)) throw new Error('Chain unavailable');
      setState(previous => {
        const seen = new Set();
        const list = [...(offset && previous.key === key ? previous.list || [] : []), ...response.list]
          .filter(item => item?.id && item.id !== reaction.id && !seen.has(item.id) && seen.add(item.id));
        return { key, list, next: response.next_offset, loading: false, error: false };
      });
    } catch {
      if (generation === version.current) setState(previous => ({ ...previous, loading: false, error: true }));
    } finally {
      if (generation === version.current) pending.current = false;
      if (controller.current === request) controller.current = null;
    }
  }, [reaction.id, key, available]);
  useEffect(() => {
    setState(previous => previous.key === key ? previous : { ...emptyChain, key });
    if (enabled) load(0);
    return () => { version.current += 1; pending.current = false; controller.current?.abort(); };
  }, [enabled, load, key]);
  return { ...current, loaded: current.list !== null, list: current.list ?? (reaction.quote ? [reaction.quote] : []),
    loadMore: () => load(current.next ?? 0), retry: () => load(requestedOffset.current) };
}
